import { Modal, Form, notification } from "antd";
import { useCallback } from "react";
import { isEmpty } from "lodash";

import MentionsComponent from "shared/components/mentions/Mentions";
import { useCreateNoteMutation } from "notes/queries";
import { useUserMentionOptions } from "users/queries";
import { useBoundStore } from "store";
import "./AddNoteModal.scss";

type Props = {
  open: boolean;
  projectId: string;
  onClose: () => void;
  onSuccess?: () => void;
};

export const AddNoteModal = ({ open, projectId, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm();
  const [api, notificationContextHolder] = notification.useNotification();
  const { mutateAsync: createNote, isPending } = useCreateNoteMutation();
  const { currentOrganization } = useBoundStore();
  const { data: userMentionOptions } = useUserMentionOptions(currentOrganization?.id);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      if (!values.note?.content) {
        return;
      }
      await createNote(
        {
          projectId,
          note: {
            content: values.note.content,
            mentionIds: values.note.mentionIds,
          },
        },
        {
          onSuccess: () => {
            api.success({
              duration: 3,
              message: "Note added",
              description: "Your note has been added successfully.",
            });
            form.resetFields();
            onSuccess?.();
            onClose();
          },
          onError: (error) => {
            api.error({
              message: "Error adding note",
              description: error?.message,
            });
          },
        }
      );
    } catch {
      // Validation failed
    }
  }, [form, createNote, projectId, api, onSuccess, onClose]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  return (
    <>
      {notificationContextHolder}
      <Modal
        title="Add Note"
        open={open}
        onOk={handleSubmit}
        onCancel={handleCancel}
        okText="Add Note"
        okButtonProps={{ loading: isPending }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" className="add-note-modal-form">
          <Form.Item
            name="note"
            rules={[
              {
                required: true,
                message: "Please enter a note.",
              },
            ]}
          >
            <MentionsComponent
              options={userMentionOptions ?? []}
              onChange={(content, mentionIds) => {
                if (isEmpty(content)) {
                  form.setFieldValue("note", undefined);
                  return;
                }
                form.setFieldValue("note", {
                  content,
                  mentionIds,
                });
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
